import ToastWrapper, { toastDefaultProps } from './ToastWrapper'

const defaultWrapperId = 'default'
const wrappers = new Map()

function castPlacment (placement) {
    if (/\top\b/.test(placement)) {
        return 'top-full'
    }

    if (/\bottom\b/.test(placement)) {
        return 'bottom-full'
    }
}

async function createWrapper(wrapperId, props) {

    const [ wrapper ] = await ToastWrapper.getInstance(props)

    wrappers.set(wrapperId || defaultWrapperId, wrapper)

    return wrapper
}

function getWrapper(wrapperId) {
    if (wrappers.size === 0) {
        return null
    }
    return wrappers.get(wrapperId || defaultWrapperId)
}

function pushToWrapper(message, options = toastDefaultProps) {
    let id = options.placement
    if (options.block) {
        id = castPlacment(options.placement)
    }

    let wrapper = getWrapper(id)

    if (wrapper?.current) {
        return wrapper.current.push(message)
    }

    return createWrapper(id ?? '', options).then(ref => {
        return ref.current?.push(message)
    })
}

/**
 * The same words twice within a few seconds are one message. A failed save
 * showed the server's reason from the request layer AND the same reason again
 * from the page's own error handler; a failed load showed it once per retry.
 * The window never outlasts the first toast's own time on screen, so a repeat
 * is only dropped while the original is still showing.
 */
export const DEDUPE_WINDOW_MS = 3000
const recentlyShown = new Map<string, { until: number; result: unknown }>()

const textOf = (node): string | null => {
    if (node === null || node === undefined || typeof node === 'boolean') return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) {
        const parts = node.map(textOf)
        return parts.some((p) => p === null) ? null : parts.join('')
    }
    // Anything richer than plain text (an element, a render function) is never merged.
    return null
}

export const dedupeKeyOf = (message): string | null => {
    const props = message?.props
    if (!props) return null
    const title = textOf(props.title)
    const body = textOf(props.children)
    if (title === null || body === null || (title === '' && body === '')) return null
    return `${props.type || ''}\u0000${title}\u0000${body}`
}

const toast = (message) => toast.push(message)

toast.push = (message, options = toastDefaultProps) => {
    const now = Date.now()
    recentlyShown.forEach((entry, key) => {
        if (entry.until <= now) recentlyShown.delete(key)
    })

    const dedupeKey = dedupeKeyOf(message)
    const shown = dedupeKey ? recentlyShown.get(dedupeKey) : undefined
    if (shown) return shown.result

    const result = pushToWrapper(message, options)
    if (dedupeKey) {
        const duration = message?.props?.duration
        const windowMs = duration > 0 ? Math.min(duration, DEDUPE_WINDOW_MS) : DEDUPE_WINDOW_MS
        recentlyShown.set(dedupeKey, { until: now + windowMs, result })
    }
    return result
}

toast.remove = (key) => {
    wrappers.forEach(elm => elm.current.remove(key))
}

toast.removeAll = () => {
    wrappers.forEach(elm => elm.current.removeAll())
}

export default toast